import {ApiMethods, ApiRequestDs, ApiService as BizkeytechApiService} from "../../../@bizkeytech";
import * as models from "./type-declarations";
import ApiEndpoints from "../../models/api-endpoints";

/**
 * The interface that houses all the available api calls related to this application.
 */
class ApiService extends BizkeytechApiService {

    // ###########################      BIZ FILE        ###################################

    /**
     * Creates a new BizFile access token.
     *
     * @param token                 The token of the operator.
     * @param abortController       The abort controller that will be used to cancel the request.
     * @returns                     The response from the server.
     */
    public static async createBizFileAccessToken(token?: string, abortController?: AbortController): models.CreateBizFileAccessTokenResponse {
        return await this.executor.execute({
            method: ApiMethods.get,
            url: ApiEndpoints.bizFile.createAccessToken,
            signal: abortController?.signal,
            enforceAuth: false,
            showErrorToast: false,
            headers: {
                Authorization: token ?? "00000000-0000-0000-0000-000000000000",
            },
        });
    }

    /**
     * Uploads the initial file chunk to the server.
     *
     * @param request               The argument to upload the initial file chunk.
     * @param onUploadProgress      The callback that will be called when the upload progress changes.
     * @param abortController       The abort controller that will be used to cancel the request.
     * @returns                     The response from the server.
     */
    public static async uploadFile(
        request: models.UploadFileRequest,
        onUploadProgress: ApiRequestDs['onUploadProgress'],
        abortController?: AbortController
    ): models.UploadFileResponse {
        const formData = new FormData();
        formData.append('firstChunk', request.file, request.fileName);
        formData.append('fileSize', request.file, request.fileSize.toString());
        return await this.executor.execute({
            method: ApiMethods.post,
            url: ApiEndpoints.bizFile.uploadFile,
            data: formData,
            onUploadProgress: onUploadProgress,
            signal: abortController?.signal,
        });
    }

    /**
     * Submits a file chunk to the server.
     *
     * @param request               The request to upload a file chunk.
     * @param onUploadProgress      The callback that will be called when the upload progress changes.
     * @param abortController       The abort controller that will be used to cancel the request.
     * @returns                     The response from the server.
     */
    public static async uploadRemainingChunk(
        request: models.UploadRemainingChunkRequest,
        onUploadProgress: ApiRequestDs['onUploadProgress'],
        abortController?: AbortController
    ): models.UploadRemainingChunkResponse {
        const formData = new FormData();
        formData.append('nextChunk', request.nextChunk, request.nextChunkFileName);
        formData.append('fileId', request.fileId);
        return await this.executor.execute({
            method: ApiMethods.post,
            url: ApiEndpoints.bizFile.uploadRemainingChunk,
            data: formData,
            onUploadProgress,
            signal: abortController?.signal,
        })
    }


    // ###########################      CONFIG        ###################################

    /**
     * Get the configuration of the site from the srver.
     *
     * @param abortController       The abort controller that will be used to cancel the request.
     */
    static async getConfig(abortController: AbortController): models.GetConfigResponse {
        return await this._executor.execute({
            method: ApiMethods.get,
            url: ApiEndpoints.config.getConfig,
            signal: abortController.signal,
            headers: {
                Authorization: "00000000-0000-0000-0000-000000000000",
            },
        });
    }


    // ###########################      OPERATOR        ###################################

    /**
     * Fetches the current operator information from the server.
     *
     * @param token             The token of the operator.
     * @param abortController   The abort controller that will be used to cancel the request.
     */
    static async getCurrentOperatorInformation(token: string, abortController: AbortController): models.GetCurrentOperatorInformationResponse {
        return await this._executor.execute({
            method: ApiMethods.get,
            url: ApiEndpoints.operator.getCurrentOperatorInformation,
            signal: abortController.signal,
            headers: {
                Authorization: token,
            },
        });
    }

    /**
     * Fetches the current partner information from the server.
     *
     * @param token             The token of the partner.
     * @param abortController   The abort controller that will be used to cancel the request.
     * @returns                 The response from the server.
     */
    static async getPartnerInformation(token: string, abortController?: AbortController): models.GetPartnerInformationResponse {
        return await this._executor.execute({
            method: ApiMethods.get,
            url: ApiEndpoints.operator.getPartnerInformation,
            signal: abortController?.signal,
            showErrorToast: false,
            headers: {
                Authorization: token,
            },
        });
    }

    // ###########################      REPORTING TOOLS        ###################################

    /**
     * Fetches the list of task status groups from the server.
     * @param reportName        the name of the section to fetch the columns for.
     * @param abortController   the abort controller to cancel the request.
     */
    static async fetchDynamicColumns(reportName: string, abortController: AbortController): models.FetchDynamicColumnsResponse {
        return await this._executor.execute({
            method: ApiMethods.post,
            url: ApiEndpoints.reportingTools.getColumns,
            data: {
                reportName: reportName,
            },
            signal: abortController.signal,
        });
    }


    // ###########################      DASHBOARD        ###################################

    /**
     * Fetches the dashboard data from the server.
     * @param abortController   the abort controller to cancel the request.
     */
    static async getDashboard(abortController?: AbortController): models.GetDashboardResponse {
        return await this._executor.execute({
            method: ApiMethods.get,
            url: ApiEndpoints.dashboard.getDashboard,
            signal: abortController?.signal,
        });
    }


    // ###########################      CODE        ###################################

    /**
     * Fetches the code from the server.
     * @param id                    the id of the code to fetch.
     * @param abortController       the abort controller to cancel the request.
     */
    static async getCode(id: string, abortController?: AbortController): models.GetCodeResponse {
        return await this._executor.execute({
            method: ApiMethods.get,
            url: ApiEndpoints.code.get(id),
            showErrorToast: true,
            signal: abortController?.signal,
        });
    }

    /**
     * Creates a code on the server.
     * @param data                  the data to create the code with.
     * @param abortController       the abort controller to cancel the request.
     */
    static async createCode(data: models.CreateCodeRequestDs, abortController?: AbortController): models.CreateCodeResponse {
        return await this._executor.execute({
            method: ApiMethods.post,
            url: ApiEndpoints.code.create,
            signal: abortController?.signal,
            data: data,
        });
    }

    /**
     * Updates a code on the server.
     * @param data                      the data to update the code with.
     * @param abortController           the abort controller to cancel the request.
     */
    static async updateCode(data: models.UpdateCodeRequestDs, abortController?: AbortController): models.UpdateCodeResponse {
        return await this._executor.execute({
            method: ApiMethods.put,
            url: ApiEndpoints.code.update,
            signal: abortController?.signal,
            data: data,
        });
    }

    /**
     * Deletes a code on the server.
     * @param id                    the id of the code to delete.
     * @param abortController       the abort controller to cancel the request.
     */
    static async deleteCode(id: string, abortController?: AbortController): models.DeleteCodeResponse {
        return await this._executor.execute({
            method: ApiMethods.delete,
            url: ApiEndpoints.code.delete(id),
            signal: abortController?.signal,
        });
    }

    /**
     * Scans a code on the server.
     * @param id                    the id of the code to scan.
     * @param abortController       the abort controller to cancel the request.
     */
    static async scanCode(id: string, abortController?: AbortController): models.ScanCodeResponse {
        return await this._executor.execute({
            method: ApiMethods.get,
            url: ApiEndpoints.code.scan(id),
            signal: abortController?.signal,
            headers: {
                Authorization: "00000000-0000-0000-0000-000000000000",
            }
        });
    }

    /**
     * Fetches the list of codes from the server.
     *
     * @param data                  the data to fetch the codes with.
     * @param abortController       the abort controller to cancel the request.
     */
    static async listCodes(data: models.ListCodeRequestDs, abortController?: AbortController): models.ListCodeResponse {
        return await this._executor.execute({
            method: ApiMethods.post,
            url: ApiEndpoints.code.list,
            signal: abortController?.signal,
            data: data,
        });
    }

    /**
     * Fetches the code report from the server.
     *
     * @param data                  the data to fetch the code report with.
     * @param abortController       the abort controller to cancel the request.
     */
    static async getCodeReport(data: models.GetCodeReportRequestDs, abortController?: AbortController): models.GetCodeReportResponse {
        return await this._executor.execute({
            method: ApiMethods.post,
            url: ApiEndpoints.code.getReport,
            signal: abortController?.signal,
            data: data,
        });
    }


}


export * from './type-declarations';
export default ApiService;
